/**
 * UnifiedSavingsBanner — Dashboard hero
 *
 * Ties together the two halves of CARTIE's value:
 *   1. Cloud waste reclaimed (engines 1-26 → anomalies + recommendations)
 *   2. AI tokens saved (Auto-Compiler Proxy → /api/llm-proxy/savings)
 *
 * Shows month-to-date savings + the FinOps Foundation 22/22 capability badge.
 * Designed to be the visible proof surface that the CFO sees first thing.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, Cloud, Cpu, ArrowRight, ShieldCheck, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  if (v >= 100)  return `$${v.toFixed(0)}`;
  return `$${v.toFixed(2)}`;
};

export default function UnifiedSavingsBanner() {
  const [cloudPotential, setCloudPotential] = useState(null);
  const [cloudRealized, setCloudRealized] = useState(null);
  const [aiSavings, setAiSavings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    const headers = { Authorization: `Bearer ${token}` };

    // v35.08 — Single unified ledger endpoint replaces the two prior calls.
    // Falls back to legacy endpoints only if the new one is unavailable.
    axios.get(`${API}/savings/ledger/totals`, { headers }).then((r) => {
      if (!alive) return;
      setAiSavings(Number(r.data?.ai_realized_usd || 0));
      setCloudPotential(Number(r.data?.cloud_potential_usd || 0));
      setCloudRealized(Number(r.data?.cloud_realized_usd || 0));
      setLoading(false);
    }).catch(() => {
      // Legacy fallback
      const fetchCloud = axios.get(`${API}/recommendations`, { headers }).then((r) => {
        const items = r.data?.items || r.data || [];
        return items.reduce((a, x) => a + (Number(x.estimated_savings || x.savings || 0)), 0);
      }).catch(() => 0);
      const fetchAI = axios.get(`${API}/llm-proxy/savings`, { headers }).then((r) => {
        return Number(r.data?.total_savings_usd || 0);
      }).catch(() => 0);
      Promise.all([fetchCloud, fetchAI]).then(([c, a]) => {
        if (!alive) return;
        setCloudPotential(c);
        setCloudRealized(0);
        setAiSavings(a);
        setLoading(false);
      });
    });

    return () => { alive = false; };
  }, []);

  // Total presented is REALIZED ai + REALIZED cloud — honest, never collapses
  // "potential" into "saved".
  const total = (aiSavings || 0) + (cloudRealized || 0);
  const hasData = total > 0 || (cloudPotential || 0) > 0;
  // Legacy alias for existing test selectors
  const cloudSavings = cloudPotential;

  return (
    <div
      className="relative rounded-2xl bg-slate-950 bg-gradient-to-br from-slate-950 via-emerald-950/30 to-slate-950 border border-emerald-500/30 shadow-xl shadow-emerald-500/10 p-5 sm:p-6 overflow-hidden"
      data-testid="unified-savings-banner"
    >
      {/* glow accents */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative grid lg:grid-cols-[1fr_auto] gap-4 items-center">
        {/* Left: headline + totals */}
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-3">
            <Sparkles className="w-3 h-3" /> Financial Operating System · Live
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight">
            {loading ? (
              <span className="inline-block w-40 h-8 bg-white/5 rounded animate-pulse" />
            ) : hasData ? (
              <>
                <span className="text-emerald-300 font-mono tabular-nums" data-testid="banner-total-savings">
                  {formatUsd(total)}
                </span>
                <span className="text-slate-200"> realized this month</span>
              </>
            ) : (
              <span className="text-slate-200">Your FinOps savings start here</span>
            )}
          </h2>
          {!loading && (cloudPotential || 0) > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[10px] font-mono uppercase tracking-widest" data-testid="banner-potential-pill">
              + {formatUsd(cloudPotential)} cloud potential · ship to realize
            </div>
          )}
          <p className="text-xs sm:text-sm text-slate-400 mt-2 leading-relaxed max-w-2xl">
            CARTIE&apos;s 26 detection engines flag every cloud cost spike, and
            The Auto-Compiler Proxy&apos;s drop-in interface auto-cuts your AI bill at runtime.
            Trace-to-code and GitHub PR auto-fixer ship Q2 2026 —{' '}
            <Link to="/roadmap" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2" data-testid="banner-roadmap-link">
              see roadmap
            </Link>.
          </p>

          {/* Two-pillar split */}
          <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-xl">
            <div className="rounded-xl border border-white/10 bg-slate-950/60 p-3" data-testid="banner-cloud-split">
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4 text-cyan-300" />
                <span className="text-[11px] uppercase tracking-widest font-mono text-cyan-300">Cloud waste reclaimed</span>
              </div>
              <div className="mt-1.5 text-xl font-bold text-white font-mono tabular-nums" data-testid="banner-cloud-amount">
                {loading ? '—' : formatUsd(cloudSavings || 0)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Engines 1-26 · idle, oversized, untagged</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-950/60 p-3" data-testid="banner-ai-split">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-emerald-300" />
                <span className="text-[11px] uppercase tracking-widest font-mono text-emerald-300">AI tokens saved</span>
              </div>
              <div className="mt-1.5 text-xl font-bold text-white font-mono tabular-nums" data-testid="banner-ai-amount">
                {loading ? '—' : formatUsd(aiSavings || 0)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Auto-Compiler · compact + cache + cascade</div>
            </div>
          </div>
        </div>

        {/* Right: CTA stack */}
        <div className="flex flex-col gap-2 lg:items-end">
          <Link
            to="/patent-portfolio"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all hover:-translate-y-0.5"
            data-testid="banner-cta-engines"
          >
            See all 26 engines <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/guarantee"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/10 text-emerald-200 hover:text-emerald-100 font-semibold text-xs transition-all"
            data-testid="banner-cta-guarantee"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> 3× ROI guarantee
          </Link>
          <Link
            to="/llm-proxy"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs text-slate-400 hover:text-emerald-300 transition-colors"
            data-testid="banner-cta-proxy"
          >
            <TrendingUp className="w-3 h-3" /> Proxy savings ledger
          </Link>
          <Link
            to="/how-it-saves"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs text-slate-400 hover:text-emerald-300 transition-colors"
            data-testid="banner-cta-how-it-saves"
          >
            <Sparkles className="w-3 h-3" /> How CARTIE saves you money
          </Link>
          <Link
            to="/how-it-works"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs text-slate-400 hover:text-emerald-300 transition-colors"
            data-testid="banner-cta-how-it-works"
          >
            <ShieldCheck className="w-3 h-3" /> How CARTIE works with your team
          </Link>
        </div>
      </div>
    </div>
  );
}
